<template>
  <div>
    <!-- Basic Info -->
    <div id="basic-info" class="card mt-4">
      <div class="card-header pb-3">
        <div v-if="authentications.locked == 1">
          <material-alert color="danger">
            Der zum Kundenkonto gehörende Zugang ist aktuell mit folgender Begründung gesperrt:
            {{ authentications.locked_reason }}<br/>
            Die Entsperrung kann unter Zugang > Einstellungen aufgehoben werden.
          </material-alert>
        </div>
        <h5>Stammdaten</h5>
        <p class="text-sm">
          Folgende Stammdaten sind aktuell für den Kunden bekannt. Die Daten werden aus dem K6-System bezogen.
        </p>
      </div>
      <div class="card-body pt-0">
        <div class="d-flex align-items-center">
          <div class="my-auto ms-3">
            <div class="h-100">
              <p class="text-sm mb-1">{{ mandant.id }}</p>
              <p class="mb-0 text-xs">Kundennummer</p>
            </div>
          </div>
        </div>
        <hr class="horizontal dark" />
        <div class="d-flex align-items-center">
          <div class="my-auto ms-3">
            <div class="h-100">
              <p class="text-sm mb-1">{{ mandant.firstname }}</p>
              <p class="mb-0 text-xs">Vorname</p>
            </div>
          </div>
          <div class="my-auto ms-3">
            <div class="h-100">
              <p class="text-sm mb-1">{{ mandant.lastname }}</p>
              <p class="mb-0 text-xs">Nachname</p>
            </div>
          </div>
        </div>
        <hr class="horizontal dark" />
        <div class="d-flex align-items-center">
          <div class="my-auto ms-3">
            <div class="h-100">
              <p class="text-sm mb-1">{{ mandant.street }}</p>
              <p class="mb-0 text-xs">Straße</p>
            </div>
          </div>
        </div>
        <hr class="horizontal dark" />
        <div class="d-flex align-items-center">
          <div class="my-auto ms-3">
            <div class="h-100">
              <p class="text-sm mb-1">{{ mandant.postcode }}</p>
              <p class="mb-0 text-xs">Postleitzahl</p>
            </div>
          </div>
          <div class="my-auto ms-3">
            <div class="h-100">
              <p class="text-sm mb-1">{{ mandant.city }}</p>
              <p class="mb-0 text-xs">Stadt</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Authentication -->
    <div id="authentication" class="card mt-4">
      <div class="card-header">
        <h5>Zugang</h5>
        <p class="text-sm">
          Jedem Kundenkonto kann ein Zugangsprofil zugeordnet werden, das aus einer E-Mail-Adresse und einem Kennwort sowie verschiedenen Einstellungen besteht.
        </p>
      </div>
      <div class="card-body pt-0">
        <!-- Kein Zugang vorhanden -->
        <div v-if="!authentications.uuid">
          <material-alert color="warning">
            Das Kontenkonto verfügt aktuell über keinen Zugang zum TWL-KOM Serviceportal.
          </material-alert>
          <material-button
            class="mt-2 mb-0"
            variant="filled"
            color="primary"
            size="sm"
            @click.once="createAccess()"
          >
            Zugang anlegen
          </material-button>
        </div>

        <!-- Zugang vorhanden -->
        <div v-else>
          <div class="d-flex align-items-center">
            <div class="my-auto ms-3">
              <div class="h-100">
                <p class="text-sm mb-1">{{ authentications.uuid }}</p>
                <p class="mb-0 text-xs">Zugangsprofil UUID</p>
              </div>
            </div>
            <div class="my-auto ms-3">
              <div class="h-100">
                <p class="text-sm mb-1" id="authentications.email">{{ authentications.email }}</p>
                <p class="mb-0 text-xs">Zugangsprofil E-Mail-Adresse</p>
              </div>
            </div>
          </div>
          <hr class="horizontal dark" />
          <div class="d-flex align-items-center">
            <div class="my-auto ms-3">
              <div class="h-100">
                <p class="text-sm mb-1">{{ convertTimestampToDate(authentications.time_passwordupdated) }}</p>
                <p class="mb-0 text-xs">Letzte Kennwortänderung</p>
              </div>
            </div>
          </div>
          <br/>
          <h6>Einstellungen</h6>
          <div class="table-responsive">
            <table class="table mb-0">
              <tbody>
                <!-- Zugang gesperrt -->
                <tr>
                  <td class="ps-1" colspan="4">
                    <div class="my-auto">
                      <span class="text-dark d-block text-sm">Zugang gesperrt</span>
                      <p class="text-xs">Ist der Zugang gesperrt, ist kein Login möglich bzw. die aktuell laufende Session wird unterbrochen. Zugänge werden automatisch nach 10 erfolglosen Loginversuchen gesperrt.</p>
                    </div>
                  </td>
                  <td>
                    <div class="ms-auto">
                      <material-switch
                        id="authentications.locked"
                        :checked="authentications.locked == 1"
                        @change="updateSettings()"
                      />
                    </div>
                  </td>
                </tr>

                <!-- Google 2FA -->
                <tr>
                  <td class="ps-1" colspan="4">
                    <div class="my-auto">
                      <span class="text-dark d-block text-sm">Google Zwei-Faktor-Authentifizierung aktiv</span>
                      <p class="text-xs">
                        Der Kunde kann über das Serviceportal die Zwei-Faktor-Authentifizierung von Mail auf Google umstellen.
                        Ohne seine Google-OTP-Tokens ist kein Login im Serviceportal mehr möglich.
                        <br/><b>Wichtig:</b> Es ist nur eine Umschaltung von Google2FA auf Mail möglich. Eine Einrichtung von Google2FA als Admin ist nicht vorgesehen.
                      </p>
                    </div>
                  </td>
                  <td>
                    <div class="ms-auto">
                      <material-switch
                        id="authentications.google2fa"
                        :checked="authentications.google2fa_enabled === 1"
                        :disabled="authentications['2fa_type'] === 'mail'"
                        @change="updateSettings($event, 'google2fa')"
                      />
                    </div>
                  </td>
                </tr>

                <!-- Kennwortänderung -->
                <tr>
                  <td class="ps-1" colspan="4">
                    <div class="my-auto">
                      <span class="text-dark d-block text-sm">Kennwortänderung beim nächsten Login erforderlich</span>
                      <p class="text-xs">
                        Wenn aktiviert, muss der Kunde beim nächsten Login zwingend sein aktuelles Kennwort ändern.
                      </p>
                    </div>
                  </td>
                  <td>
                    <div class="ms-auto">
                      <material-switch
                        id="authentications.need_passwordchange"
                        :checked="authentications.need_passwordchange == 1"
                        @change="updateSettings()"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Zugangsdaten -->
          <h6>Zugangsdaten</h6>
          <p class="text-sm">
            Hat der Kunde seine Zugangsdaten vergessen, kann ihm über diese Funktion an die bekannte E-Mail-Adresse ein neues Kennwort zugesendet werden.
          </p>
          <material-button
            class="mt-2 mb-0"
            variant="filled"
            color="success"
            size="sm"
            @click.once="updateAccess()"
          >
            Zugang anpassen
          </material-button>
        </div>
      </div>
    </div>

    <!-- Last Logins -->
    <div id="sessions" class="card mt-4">
      <div class="card-header pb-3">
        <h5>Letzte erfolgreiche Logins</h5>
        <p class="text-sm">In dieser Auflistung können nachvollziehbar die letzten Logins eingesehen werden.</p>
      </div>
      <div class="card-body pt-0">
        <div class="d-flex align-items-center" v-for="login in logins" :key="login.uuid">
          <div class="text-center w-5">
            <i class="fas fa-desktop text-lg opacity-6"></i>
          </div>
          <div class="my-auto ms-3">
            <div class="h-100">
              <p class="text-sm mb-1">{{ login.agent }}</p>
              <p class="mb-0 text-xs">Am {{ convertTimestampToDateTime(login.time_created) }} mit der öffentlichen IP {{ login.ip }}</p>
            </div>
          </div>
          <material-badge color="success" size="sm" class="my-auto ms-auto me-3">Erfolgreich</material-badge>
        </div>
        <hr class="horizontal dark" />
      </div>
    </div>
  </div>
</template>

<script>
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialAlert from "@/components/MaterialAlert.vue";
import MaterialSwitch from "@/components/MaterialSwitch.vue";
import MaterialBadge from "@/components/MaterialBadge.vue";
import axios from "axios";

export default {
  name: "SideNavItem",
  components: { MaterialButton, MaterialSwitch, MaterialBadge, MaterialAlert },
  data() {
    return {
      authentications: {},
      mandant: {},
      logins: [],
    };
  },
  mounted() {
    this.getMandant();
    this.getAuthentication();
    this.getLastLogins();
  },
  methods: {
    getAuthentication() {
      const uuid = this.$route.query.uuid;
      axios.get(`${this.$path}api/admin/mandant/${uuid}/authentication`, {}, this.$settings)
        .then((response) => {
          if (response.data.response && Array.isArray(response.data.response) && response.data.response.length > 0) {
            this.authentications = response.data.response[0];
          } else {
            this.authentications = {}; // fallback, wenn response leer oder null
          }

        });
    },
    getMandant() {
      const uuid = this.$route.query.uuid;
      axios.get(`${this.$path}api/admin/mandant/${uuid}`, {}, this.$settings)
        .then((response) => { this.mandant = response.data.response[0]; });
    },
    getLastLogins() {
      axios.get(`${this.$path}api/authentication/v1/log`, {}, this.$settings)
        .then((response) => { this.logins = response.data.response; })
        .catch((error) => { if (error.response && error.response.status === 401) this.$router.push("/login"); });
    },

    createAccess() {
      const self = this;
      this.$swal.fire({
        title: "Zugang anlegen",
        html: `
          E-Mail-Adresse: <input type="text" class="swal2-input" id="new_email" placeholder="E-Mail-Adresse"><br/>
          Das Kennwort wird automatisch generiert und per Mail zugestellt.
        `,
        focusConfirm: false,
        confirmButtonColor: '#A6C516',
        confirmButtonText: 'Zugang anlegen',
        preConfirm: () => {
          const email = document.getElementById("new_email").value;
          if (!email) {
            this.$swal.showValidationMessage("Bitte eine gültige E-Mail-Adresse eingeben.");
            return false;
          }

          const formdata = {
            email,
            locked: 0,
            locked_reason: "",
            administrative: 0,
            time_passwordupdated: 0,
            need_passwordchange: 1,
            auth_failcount: 0,
            sendpasswordmail: 1,
          };

          return axios.post(`${self.$path}api/admin/mandant/${self.$route.query.uuid}/authentication`, JSON.stringify(formdata))
            .then(() => {
              self.$swal.fire({
                title: "Erfolg",
                text: "Der Zugang wurde angelegt.",
                icon: "success",
                confirmButtonText: "OK",
                confirmButtonColor: "#A6C516"
              });
              self.getAuthentication();
            })
            .catch((error) => self.$swal.fire("Fehler", error.response?.data || error.message || error));
        }
      });
    },

    updateAccess() {
      const self = this;
      const email = document.getElementById("authentications.email").innerText;
      const uuid = this.$route.query.uuid;
      const authentication_uuid = this.authentications.uuid;

      this.$swal.fire({
        title: "Zugang anpassen",
        html: `
          E-Mail-Adresse: <input type="text" class="swal2-input" id="email" value="${email}"><br/><br/>
          Kennwort zurücksetzen und per Mail versenden: <input type="checkbox" id="sendpasswordmail"><br/>
        `,
        focusConfirm: false,
        preConfirm: () => {
          const formdata = {
            email: document.getElementById("email").value,
            sendpasswordmail: document.getElementById("sendpasswordmail").checked ? 1 : 0
          };
          return axios.put(`${self.$path}api/admin/authentication/${uuid}/${authentication_uuid}/access`, JSON.stringify(formdata))
            .then(() => {
              self.$swal.fire("Erfolg", "Die Einstellungen wurden übernommen.", "success");
              self.getMandant();
              self.getAuthentication();
            })
            .catch((error) => self.$swal.fire("Fehler", error.response?.data || error.message || error));
        }
      });
    },

    updateSettings(event, type = null) {
      const self = this;
      const uuid = self.$route.query.uuid;
      const authentication_uuid = self.authentications.uuid;

      // Werte aus Checkboxen
      const locked = document.getElementById("authentications.locked").checked ? 1 : 0;
      const need_passwordchange = document.getElementById("authentications.need_passwordchange").checked ? 1 : 0;

      // 2fa_type bestimmen
      let twofa_type = self.authentications["2fa_type"];
      if (type === "google2fa") {
        if (twofa_type === "google2fa" || twofa_type === "mail") {
          twofa_type = document.getElementById("authentications.google2fa").checked ? "google2fa" : "mail";
        }
      }

      const formdata = {
        locked,
        locked_reason: self.authentications.locked_reason || "",
        need_passwordchange,
        "2fa_type": twofa_type
      };

      // Sonderfall Sperrung
      if (self.authentications.locked == 0 && locked == 1) {
        this.$swal.fire({
          title: 'Zugang sperren',
          html: 'Sperrgrund: <input type="text" class="swal2-input" id="locked_reason">',
          focusConfirm: false,
          preConfirm: () => {
            formdata["locked_reason"] = document.getElementById("locked_reason").value;
            return axios.put(`${self.$path}api/admin/authentication/${uuid}/${authentication_uuid}/settings`, JSON.stringify(formdata))
              .then(() => self.getAuthentication())
              .catch((error) => self.$swal.fire("Fehler", error.response?.data || error.message || error));
          }
        });
      } else {
        axios.put(`${self.$path}api/admin/authentication/${uuid}/${authentication_uuid}/settings`, JSON.stringify(formdata))
          .then(() => self.getAuthentication())
          .catch((error) => self.$swal.fire("Fehler", error.response?.data || error.message || error));
      }
    }
  }
};
</script>
